import { useState } from "react";
import { Loader2, Copy, Check } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email").max(320),
});

const PROMO_CODE = "MOB15";

export const NewsletterForm = ({ source = "footer" }: { source?: string }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      toast.error(parsed.error.errors[0].message, { position: "top-center" });
      return;
    }
    setLoading(true);
    const { error } = await supabase
      .from("newsletter_subscribers")
      .insert({ email: parsed.data.email, source });
    setLoading(false);
    if (error && error.code !== "23505") {
      toast.error("Couldn't subscribe. Try again.", { position: "top-center" });
      return;
    }
    setDone(true);
    setEmail("");
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      toast.success("Code copied", { position: "top-center" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy", { position: "top-center" });
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-3 animate-[page-fade_0.5s_ease-out]">
        <p className="text-background/80 text-sm font-display tracking-widest">YOU'RE IN — HERE'S YOUR 15% OFF</p>
        <button
          onClick={copyCode}
          className="group inline-flex items-center gap-3 bg-accent text-accent-foreground font-display text-2xl tracking-[0.3em] px-6 py-3 hover:scale-105 transition-transform"
        >
          {PROMO_CODE}
          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5 opacity-70 group-hover:opacity-100" />}
        </button>
        <p className="text-background/60 text-xs">Apply at checkout. One use per customer.</p>
      </div>
    );
  }

  return (
    <form className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto" onSubmit={onSubmit}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="flex-1 bg-transparent border border-background/30 px-4 py-3 text-sm placeholder:text-background/40 focus:outline-none focus:border-background"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-accent text-accent-foreground font-display px-6 py-3 text-sm hover:bg-background hover:text-foreground transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "SUBSCRIBE"}
      </button>
    </form>
  );
};
